import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { unwrap } from '../../lib/api/client'
import type {
  AutoAnswerRowOut,
  CaseCloseoutOut,
  CaseFollowupsOut,
  CaseStaffRemindersOut,
  CloseoutSettingsIn,
  CloseoutSettingsOut,
  FollowupPreviewOut,
  ReminderBatchOut,
  ReminderPreviewOut,
  StaleCloseListOut,
  TeamNotificationSettingsOut,
} from '../../lib/api/generated'
import {
  caseCloseoutApiV1CloseoutCasesCaseIdGet,
  caseFollowupsApiV1CloseoutFollowupsCaseIdGet,
  caseStaffRemindersApiV1CloseoutStaffRemindersCaseIdGet,
  closeStaleNowApiV1CloseoutStaleClosesCaseIdCloseNowPost,
  confirmStaleBacklogApiV1CloseoutStaleClosesBacklogPost,
  getSettingsApiV1CloseoutSettingsGet,
  getTeamApiV1MeNotificationsTeamGet,
  labelFollowupPreviewApiV1FollowupsPreviewEventIdLabelPost,
  listAutoAnswersApiV1CloseoutAutoAnswersGet,
  listCloseoutApiV1CloseoutGet,
  listRemindersApiV1CloseoutRemindersGet,
  listStaleClosesApiV1CloseoutStaleClosesGet,
  overrideAutoAnswerVerdictApiV1CloseoutAutoAnswersAutoAnswerIdVerdictPut,
  pauseStaleCloseApiV1CloseoutStaleClosesCaseIdPausePost,
  previewApiV1CloseoutPreviewGet,
  previewFollowupDraftsApiV1FollowupsPreviewPost,
  putSettingsApiV1CloseoutSettingsPut,
  putTeamApiV1MeNotificationsTeamPut,
  resumeStaleCloseApiV1CloseoutStaleClosesCaseIdResumePost,
  undoAutoAnswerApiV1CloseoutAutoAnswersAutoAnswerIdUndoPost,
} from '../../lib/api/generated'

/**
 * Close-out reminders and notifications (#121, #131, #157) over the generated SDK. Every read throws on failure; a
 * screen shows "couldn't load", never an empty list (absence is not evidence).
 */

export type ReminderMode = CloseoutSettingsOut['closeout_reminders']

function list<T>(out: T[] | undefined | null, what: string): T[] {
  if (!Array.isArray(out)) throw new Error(`${what} answer malformed`)
  return out
}

export const closeoutKeys = {
  all: ['closeout'] as const,
  case: (caseId: string) => ['closeout', 'case', caseId] as const,
  batch: (caseIds: readonly string[]) => ['closeout', 'batch', [...caseIds].sort().join(',')] as const,
  reminders: () => ['closeout', 'reminders'] as const,
  preview: () => ['closeout', 'preview'] as const,
  settings: () => ['closeout', 'settings'] as const,
  followups: (caseId: string) => ['closeout', 'followups', caseId] as const,
  staffReminders: (caseId: string) => ['closeout', 'staff-reminders', caseId] as const,
  autoAnswers: () => ['closeout', 'auto-answers'] as const,
  stale: () => ['closeout', 'stale-closes'] as const,
  followupPreview: () => ['closeout', 'followup-preview'] as const,
}

/** #363 calls Riley + the unattended guard but no send/Desk transport; the result is an auditable review batch. */
export function usePreviewFollowups() {
  return useMutation({
    mutationFn: async (): Promise<FollowupPreviewOut> =>
      unwrap(previewFollowupDraftsApiV1FollowupsPreviewPost({ query: { limit: 8, include_stale: true } })),
  })
}

export function useLabelFollowupPreview() {
  return useMutation({
    mutationFn: async (input: { eventId: number; label: 'approve' | 'reject' }) =>
      unwrap(
        labelFollowupPreviewApiV1FollowupsPreviewEventIdLabelPost({
          path: { event_id: input.eventId },
          body: { label: input.label },
        }),
      ),
  })
}

/** GET-safe projection of customer waits on the #342 notice/close path. */
export const staleClosesQuery = () =>
  queryOptions({
    queryKey: closeoutKeys.stale(),
    queryFn: async ({ signal }): Promise<StaleCloseListOut> => {
      const out = await unwrap(listStaleClosesApiV1CloseoutStaleClosesGet({ signal }))
      if (!out || !Array.isArray(out.rows)) throw new Error('stale-close answer malformed')
      return out
    },
    staleTime: 30_000,
    retry: 1,
  })

function useRefreshStale() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: closeoutKeys.stale() })
    void qc.invalidateQueries({ queryKey: closeoutKeys.reminders() })
  }
}

export function useConfirmStaleBacklog() {
  const refresh = useRefreshStale()
  return useMutation({
    mutationFn: async (input: { all: boolean; caseIds: string[] }) =>
      unwrap(
        confirmStaleBacklogApiV1CloseoutStaleClosesBacklogPost({
          body: { confirm: true, all: input.all, case_ids: input.all ? [] : input.caseIds },
        }),
      ),
    onSuccess: refresh,
  })
}

export function usePauseStaleClose() {
  const refresh = useRefreshStale()
  return useMutation({
    mutationFn: async (input: { caseId: string; reason: string }) =>
      unwrap(
        pauseStaleCloseApiV1CloseoutStaleClosesCaseIdPausePost({
          path: { case_id: input.caseId },
          body: { reason: input.reason },
        }),
      ),
    onSuccess: refresh,
  })
}

export function useResumeStaleClose() {
  const refresh = useRefreshStale()
  return useMutation({
    mutationFn: async (caseId: string) =>
      unwrap(resumeStaleCloseApiV1CloseoutStaleClosesCaseIdResumePost({ path: { case_id: caseId }, body: { confirm: true } })),
    onSuccess: refresh,
  })
}

export function useCloseStaleNow() {
  const refresh = useRefreshStale()
  return useMutation({
    mutationFn: async (caseId: string) =>
      unwrap(closeStaleNowApiV1CloseoutStaleClosesCaseIdCloseNowPost({ path: { case_id: caseId }, body: { confirm: true } })),
    onSuccess: refresh,
  })
}

export const autoAnswersQuery = () =>
  queryOptions({
    queryKey: closeoutKeys.autoAnswers(),
    queryFn: async ({ signal }): Promise<AutoAnswerRowOut[]> =>
      list(await unwrap(listAutoAnswersApiV1CloseoutAutoAnswersGet({ query: { limit: 200 }, signal })), 'auto-answer comparisons'),
    staleTime: 30_000,
    retry: 1,
  })

export function useOverrideAutoAnswer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { id: string; verdict: 'agree' | 'disagree'; reason: string }) =>
      unwrap(
        overrideAutoAnswerVerdictApiV1CloseoutAutoAnswersAutoAnswerIdVerdictPut({
          path: { auto_answer_id: input.id },
          body: { verdict: input.verdict, reason: input.reason },
        }),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: closeoutKeys.autoAnswers() }),
  })
}

export function useUndoAutoAnswer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { id: string; reason: string }) =>
      unwrap(
        undoAutoAnswerApiV1CloseoutAutoAnswersAutoAnswerIdUndoPost({
          path: { auto_answer_id: input.id },
          body: { reason: input.reason },
        }),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: closeoutKeys.autoAnswers() }),
  })
}

/** The case's staff/contractor reminder trail (#1006). Computed on the server; the GET writes nothing. */
export const caseStaffRemindersQuery = (caseId: string) =>
  queryOptions({
    queryKey: closeoutKeys.staffReminders(caseId),
    queryFn: async ({ signal }): Promise<CaseStaffRemindersOut> => {
      const out = await unwrap(caseStaffRemindersApiV1CloseoutStaffRemindersCaseIdGet({ path: { case_id: caseId }, signal }))
      if (!out || !Array.isArray(out.rows)) throw new Error('staff-reminder answer malformed')
      return out
    },
    staleTime: 60_000,
    retry: 1,
  })

/** The case's waiting-on-customer follow-up trail (#216). Computed on the server; the GET writes nothing. */
export const caseFollowupsQuery = (caseId: string) =>
  queryOptions({
    queryKey: closeoutKeys.followups(caseId),
    queryFn: async ({ signal }): Promise<CaseFollowupsOut> => {
      const out = await unwrap(caseFollowupsApiV1CloseoutFollowupsCaseIdGet({ path: { case_id: caseId }, signal }))
      if (!out || !Array.isArray(out.rows)) throw new Error('follow-up answer malformed')
      return out
    },
    staleTime: 60_000,
    retry: 1,
  })

export const caseCloseoutQuery = (caseId: string) =>
  queryOptions({
    queryKey: closeoutKeys.case(caseId),
    queryFn: async ({ signal }): Promise<CaseCloseoutOut> => {
      const out = await unwrap(caseCloseoutApiV1CloseoutCasesCaseIdGet({ path: { case_id: caseId }, signal }))
      if (!out || !Array.isArray(out.blockers)) throw new Error('close-out answer malformed')
      return out
    },
    staleTime: 60_000,
    retry: 1,
  })

/** At most this many case ids per batched read, so the query string stays well under proxy limits. */
export const CLOSEOUT_BATCH = 100

/**
 * The batched `GET /api/v1/closeout?case_id=…` answer for queue rows (#121), keyed by case id. `blocked_only=false` so
 * a row whose rules could not all be checked still gets its "unknown" chip.
 */
export const closeoutBatchQuery = (caseIds: readonly string[]) =>
  queryOptions({
    queryKey: closeoutKeys.batch(caseIds),
    queryFn: async ({ signal }): Promise<Map<string, CaseCloseoutOut>> => {
      const out = await unwrap(listCloseoutApiV1CloseoutGet({ query: { case_id: [...caseIds], blocked_only: false }, signal }))
      if (!out || !Array.isArray(out.cases)) throw new Error('close-out answer malformed')
      return new Map(out.cases.map((c) => [c.case_id, c]))
    },
    enabled: caseIds.length > 0,
    staleTime: 60_000,
    retry: 1,
  })

export const remindersQuery = () =>
  queryOptions({
    queryKey: closeoutKeys.reminders(),
    queryFn: async ({ signal }): Promise<ReminderBatchOut[]> =>
      list(await unwrap(listRemindersApiV1CloseoutRemindersGet({ query: { limit: 200 }, signal })), 'reminders'),
    staleTime: 30_000,
    retry: 1,
  })

/** How far back the sidebar's Follow-ups counter looks for shadow drafts (runs are daily on weekdays). */
export const DRAFT_COUNT_DAYS = 7

/** Shadow drafts recorded in the last {@link DRAFT_COUNT_DAYS} days: the sidebar's Follow-ups counter. */
export function recentDraftCount(rows: ReminderBatchOut[], now: number = Date.now()): number {
  const since = now - DRAFT_COUNT_DAYS * 24 * 60 * 60 * 1000
  return rows.filter((b) => b.status === 'drafted' && new Date(b.at).getTime() >= since).length
}

export const previewQuery = () =>
  queryOptions({
    queryKey: closeoutKeys.preview(),
    queryFn: async ({ signal }): Promise<ReminderPreviewOut> => {
      const out = await unwrap(previewApiV1CloseoutPreviewGet({ signal }))
      if (!out || !Array.isArray(out.batches)) throw new Error('preview answer malformed')
      return out
    },
    staleTime: 30_000,
    retry: 1,
  })

export const closeoutSettingsQuery = () =>
  queryOptions({
    queryKey: closeoutKeys.settings(),
    queryFn: async ({ signal }): Promise<CloseoutSettingsOut> => {
      const out = await unwrap(getSettingsApiV1CloseoutSettingsGet({ signal }))
      if (!out || typeof out.closeout_reminders !== 'string') throw new Error('settings answer malformed')
      return out
    },
    staleTime: 60_000,
    retry: 1,
  })

export function useSaveCloseoutSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: CloseoutSettingsIn) => unwrap(putSettingsApiV1CloseoutSettingsPut({ body })),
    onSuccess: (saved) => {
      qc.setQueryData(closeoutKeys.settings(), saved)
      void qc.invalidateQueries({ queryKey: closeoutKeys.all })
    },
  })
}

export const notificationKeys = {
  team: () => ['notifications', 'team'] as const,
}

export const teamNotificationsQuery = () =>
  queryOptions({
    queryKey: notificationKeys.team(),
    queryFn: async ({ signal }): Promise<TeamNotificationSettingsOut> => {
      const out = await unwrap(getTeamApiV1MeNotificationsTeamGet({ signal }))
      if (!out || typeof out.desk_nudges !== 'boolean') throw new Error('notification settings answer malformed')
      return out
    },
    staleTime: 60_000,
    retry: 1,
  })

export function useSaveTeamNotifications() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (desk_nudges: boolean) => unwrap(putTeamApiV1MeNotificationsTeamPut({ body: { desk_nudges } })),
    onSuccess: (saved) => qc.setQueryData(notificationKeys.team(), saved),
  })
}

/** "user:<id>" / "firm:<id>" / "vendor:<id>" / "customer:<case id>" -> what kind of party a batch went to. */
export function recipientKind(key: string): 'person' | 'contractor' | 'vendor' | 'customer' | 'unknown' {
  const kind = key.split(':', 1)[0]
  if (kind === 'user') return 'person'
  if (kind === 'firm') return 'contractor'
  if (kind === 'vendor') return 'vendor'
  if (kind === 'customer') return 'customer'
  return 'unknown'
}

/** Human words for the reason codes the engine records (raw code kept in the title for the curious). */
export function reasonText(code: string): string {
  const raw = code.startsWith('would_block:') ? code.slice('would_block:'.length) : code
  const words: Record<string, string> = {
    no_vendor_conversation: 'no vendor conversation on the case yet',
    no_address: 'no address on file',
    customer_address: "the address is the customer's",
    guard_unavailable: 'the outbound guard is unavailable',
    secret_check_unavailable: "the customer's secret list could not be read",
    every_item_blocked: 'every line was blocked by the disclosure check',
    portal_link_unavailable: 'no portal link could be issued',
    no_ticket_for_guard: 'no Desk ticket to guard against',
    no_contractor_channel: 'no contractor channel configured',
    no_vendor_channel: 'no vendor channel configured',
    snoozed: 'the case is snoozed',
    next_move_future: 'a next move is not due yet',
    appointment_future: 'an appointment is coming up',
    ticket_spam: 'Desk marks the ticket as spam',
    no_approver: 'no manager recorded as turning auto on',
    riley_identity_unavailable: "Riley's Desk identity is not configured",
    warnings_unacknowledged: 'a guard warning nobody can accept automatically',
    mirror_stale: 'the Desk mirror is stale',
    no_channel: 'the customer send path is not wired',
  }
  const text = words[raw] ?? raw.replaceAll('_', ' ').replaceAll(':', ': ')
  return code.startsWith('would_block:') ? `would block: ${text}` : text
}
